import { ReactElement } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { PageTabs, type PageTab } from "@components/CatalogIndex";
import CompanyInfoPage from "./CompanyInfo";
import PasswordPage from "./Password";
import Features from "./CompanyInfo/Features";
import NotificationsPage from "./Notifications";
import "./styles.css";
import Menu from "./Menu";
import { MenuOption } from "./types";
import { checkPrivilege } from "@utils/helpers";
import { Privileges, AccessLevels } from "../../../types";
import useWhoAmI from "./api/useWhoAmI";
import useFlags from "@common/hooks/useFlags";
import {
  SETTINGS_NAV_HEIGHT,
  SETTINGS_HEADER_HEIGHT,
  settingsTitleStyle,
} from "../../../layouts/SettingsLayout/SettingsNavigationProvider";

type MenuOptionConfig = {
  path: string;
  label: string;
  element: ReactElement;
  privilege?: Privileges;
  accessLevel?: AccessLevels;
  enabled?: boolean;
};

interface EditAccountProps {
  sidebarWidth?: number;
}

const EditAccount = ({ sidebarWidth }: EditAccountProps): JSX.Element => {
  const { data: user, error, isLoading } = useWhoAmI();
  const {
    releaseUserFlagManagement,
    releaseNotificationManagement,
    releaseReactNav,
  } = useFlags();
  const navigate = useNavigate();
  const { user_id } = useParams();
  const { pathname } = useLocation();

  if (isLoading || !user || error) {
    return <></>;
  }

  const { privileges } = user.attributes;

  const menuOptionsMap: Record<MenuOption, MenuOptionConfig> = {
    [MenuOption.Contact]: {
      path: "contact",
      label: "Personal Info",
      element: <CompanyInfoPage />,
    },
    [MenuOption.Preferences]: {
      path: "preferences",
      label: "Preferences",
      element: <Features />,
      privilege: Privileges.SETTINGS_USER_PREFERENCES,
      accessLevel: AccessLevels.MANAGE,
      enabled: releaseUserFlagManagement,
    },
    [MenuOption.Notifications]: {
      path: "notifications",
      label: "Notifications",
      element: <NotificationsPage />,
      enabled: releaseNotificationManagement,
    },
    [MenuOption.Password]: {
      path: "password",
      label: "Password",
      element: <PasswordPage />,
    },
  };

  const menuOptions = Object.entries(menuOptionsMap)
    .filter(([option]) => {
      const { privilege, accessLevel, enabled } = menuOptionsMap[option];
      return (
        enabled !== false &&
        (!privilege ||
          checkPrivilege({
            userPrivileges: privileges || [],
            privilege,
            accessLevel,
          }))
      );
    })
    .map(([option]) => option as MenuOption);

  // Shared sub-page routing — identical in both chromes; only the nav around it
  // differs by release.react-nav.
  const routes = (
    <Routes>
      {menuOptions.map((option) => {
        const config = menuOptionsMap[option];
        return (
          <Route key={option} path={config.path} element={config.element} />
        );
      })}
      <Route
        index
        element={<Navigate to={menuOptionsMap[menuOptions[0]].path} replace />}
      />
      <Route path="*" element={<div>Page not found</div>} />
    </Routes>
  );

  // release.react-nav OFF → classic chrome: keep the original left-side vertical
  // menu so the page matches the surrounding classic layout.
  if (!releaseReactNav) {
    const menuItems = menuOptions.map((option) => ({
      eventKey: menuOptionsMap[option].path.replace(/\/\*$/, ""),
      label: menuOptionsMap[option].label,
    }));

    return (
      <Row className="myAccount">
        <Col
          sm={3}
          className="left-menu4"
          style={{ padding: "20px 0px 0px 46px", maxWidth: "20%" }}
        >
          <Menu items={menuItems} />
        </Col>

        <Col sm={9} style={{ padding: "25px 0px 0px" }}>
          <div className="tabContent">{routes}</div>
        </Col>
      </Row>
    );
  }

  // release.react-nav ON → new chrome: top underline tabs above full-width content,
  // matching the project/settings nav and admin edit pages.
  const tabs: PageTab[] = menuOptions.map((option) => ({
    key: menuOptionsMap[option].path,
    label: menuOptionsMap[option].label,
  }));

  // Derive the active tab from the current sub-route so styling stays correct on
  // refresh/direct navigation, not just on click: the segment after `/edit/`.
  const pathParts = pathname.toLowerCase().split("/");
  const activeKey = pathParts[pathParts.indexOf("edit") + 1] || "";

  const handleSelect = (key: string) =>
    navigate(`/users/${user_id}/edit/${key}`);

  return (
    <div className="myAccount" style={{ marginTop: SETTINGS_HEADER_HEIGHT }}>
      <div className="myAccountNav" style={{ left: sidebarWidth ?? 0 }}>
        <h1 style={settingsTitleStyle}>User Profile</h1>
        <div
          style={{
            height: SETTINGS_NAV_HEIGHT,
            display: "flex",
            alignItems: "center",
          }}
        >
          <PageTabs activeKey={activeKey} onSelect={handleSelect} tabs={tabs} />
        </div>
      </div>
      <div className="tabContent">{routes}</div>
    </div>
  );
};

export default EditAccount;
